import { DateTime } from 'luxon';

import { LIMITED_ACCESS } from './constants';
import { createEmptyHandlingUnit } from '../components/freight-booking-wizard/create-empty-handling-unit';

import type {
  FreightBookingFormState,
  WizardDeliveryServices,
  WizardHandlingCharge,
  WizardInsurance,
  WizardPickupServices,
  WizardReferences,
  WizardShipmentServices,
  WizardTradeshow,
} from '../components/freight-booking-wizard/freight-booking-wizard-types';
import type { DimensionUnit } from '../types/dimensions';
import type {
  BookFreightFulfillmentPlanRequest,
  FreightHandlingCharge,
  FreightFulfillmentPlanSaveQuoteRequest,
  FreightInsurance,
  FreightReference,
  FreightRequestAccessorials,
  FreightRequestAddress,
  FreightRequestDimensionUnit,
  FreightRequestHandlingUnit,
  SaveFreightConfigurationRequest,
  InsuranceCommodityCategory,
  InsuranceItemCondition,
} from '../types/freight-requests';
import type {
  FreightAccessorials,
  FreightAddress,
  FreightDocument,
  FreightDimensionUnit,
  FreightHandlingUnit,
  FreightQuote,
  FreightShipment,
  FreightWeightUnit,
} from '../types/freight-responses';
import type { FulfillmentPlan, FulfillmentPlanAddress } from '../types/fulfillment-plan';
import type {
  FreightClass,
  FreightHazardClass,
  FreightPackagingType,
  HazmatIdType,
  LtlAdditionalServices,
  LtlAddress,
  LtlDocument,
  LtlHandlingUnit,
  LtlQuote,
  LtlShipment,
  PackingGroup,
} from '../types/ltl';
import type { WeightUnit } from '../types/weight';

/**
 * Maps a seller weight-unit string to the backend `FreightWeightUnit`. client-common
 * cannot import the ss-ui-client `WeightUnit` enum, so we compare string literals.
 */
export const mapWeightUnit = (sellerUnit: string): FreightWeightUnit => {
  if (sellerUnit === 'Pounds' || sellerUnit === 'Pound') return 'Pounds';
  if (sellerUnit === 'Ounces' || sellerUnit === 'Ounce') return 'Ounces';
  if (sellerUnit === 'Grams' || sellerUnit === 'Gram') return 'Grams';
  if (sellerUnit === 'Kilograms' || sellerUnit === 'Kilogram') return 'Kilograms';
  return 'Pounds';
};

/**
 * Maps a seller dimension-unit string to the backend `FreightRequestDimensionUnit`.
 */
export const mapDimensionUnit = (sellerUnit: string): FreightRequestDimensionUnit => {
  if (sellerUnit === 'Centimeters' || sellerUnit === 'Centimeter') return 'Centimeters';
  return 'Inches';
};

/**
 * Maps a wizard/browser `LtlAddress` to the backend `FreightRequestAddress`.
 * Faithful port of the old freight-quote-context mapper: keeps the `||` fallbacks
 * for string fields (identical behavior to `??` for non-empty strings, but the
 * `||` collapses empty strings to the default — preserved intentionally).
 */
export const mapAddressToBackend = (addr: LtlAddress | null, isLimitedAccess = false): FreightRequestAddress | null => {
  if (!addr) return null;
  return {
    line1: addr.line1 || null,
    line2: addr.line2 || null,
    city: addr.city || null,
    state: addr.state || null,
    postalCode: addr.postalCode || null,
    countryCode: addr.countryCode || 'US',
    company: addr.company || null,
    phone: addr.phone,
    // NOTE: the old context also emitted `email`, but the typed `FreightRequestAddress`
    // has no email field, so it is dropped here (canonical typed shape).
    name: addr.name || null,
    verificationStatus: addr.verificationStatus ?? null,
    verificationMessage: addr.verificationMessage ?? null,
    verificationUtc: addr.verificationUtc ?? null,
    lockAddress: addr.lockAddress ?? false,
    locationType: isLimitedAccess ? LIMITED_ACCESS : null,
  };
};

/**
 * Maps the wizard/browser handling units to the canonical backend handling-unit
 * payload. `weightUnit`/`dimensionUnit` are passed in (already mapped) rather than
 * closed over. Applies canonical defaults: packagingType 'Pallet', quantity 1,
 * isStackable false; commodity freightClass '_50', description 'General Merchandise'.
 */
export const mapHandlingUnitsToPayload = (
  handlingUnits: LtlHandlingUnit[],
  weightUnit: FreightWeightUnit,
  dimensionUnit: FreightRequestDimensionUnit,
): FreightRequestHandlingUnit[] => {
  return (handlingUnits || []).map((hu) => ({
    handlingUnitId: hu.handlingUnitId ?? null,
    packagingType: hu.packagingType || 'Pallet',
    quantity: hu.quantity || 1,
    length: hu.length,
    width: hu.width,
    height: hu.height,
    dimensionUnit: (hu.dimensionUnit as FreightRequestDimensionUnit | undefined) ?? dimensionUnit,
    isStackable: hu.isStackable || false,
    commodities: (hu.commodities || []).map((c) => ({
      commodityId: c.commodityId ?? null,
      freightClass: c.freightClass || '_50',
      description: c.description || 'General Merchandise',
      nmfcCode: c.nmfcCode,
      quantity: c.numberOfPieces ?? 1,
      weight: c.weight,
      weightUnit: (c.weightUnit as FreightWeightUnit | undefined) ?? weightUnit,
      packageType: c.pieceType ?? null,
      isHazmat: c.isHazmat || false,
      hazmatInfo: c.isHazmat && c.hazmatInfo ? c.hazmatInfo : null,
    })),
  }));
};

/**
 * Maps the narrow wizard service booleans (split across pickup / delivery /
 * shipment-level / tradeshow) to the is-prefixed backend `FreightRequestAccessorials`.
 */
export const mapWizardServicesToAccessorials = (
  pickup: WizardPickupServices,
  delivery: WizardDeliveryServices,
  shipmentServices: WizardShipmentServices,
  tradeshow: WizardTradeshow,
  tradeshowPickup: WizardTradeshow,
): FreightRequestAccessorials => {
  return {
    isLiftgatePickup: pickup.isLiftgatePickup,
    isLiftgateDelivery: delivery.isLiftgateDelivery,
    isInsidePickup: pickup.isInsidePickup,
    isInsideDelivery: delivery.isInsideDelivery,
    isResidentialPickup: pickup.isResidentialPickup,
    isResidentialDelivery: delivery.isResidentialDelivery,
    isNotifyBeforePickup: pickup.isNotifyBeforePickup,
    isNotifyBeforeDelivery: delivery.isNotifyBeforeDelivery,
    isConstructionSitePickup: pickup.isConstructionSitePickup,
    isConstructionSiteDelivery: delivery.isConstructionSiteDelivery,
    isSortAndSegregate: shipmentServices.isSortAndSegregate,
    isProtectionFromCold: shipmentServices.isProtectFromFreeze,
    isHoldAtTerminal: delivery.isHoldAtTerminal,
    isTradeshowPickup: pickup.isTradeshowPickup,
    isTradeshowDelivery: delivery.isTradeshowDelivery,
    tradeshowDeliveryName: tradeshow.name || null,
    tradeshowDeliveryBoothNumber: tradeshow.boothNumber || null,
    tradeshowPickupName: tradeshowPickup.name || null,
    tradeshowPickupBoothNumber: tradeshowPickup.boothNumber || null,
    isGroceryConsolidationPickup: pickup.isGroceryConsolidationPickup,
    isGroceryConsolidationDelivery: delivery.isGroceryConsolidationDelivery,
    isAppointmentDelivery: delivery.isAppointmentDelivery,
    isCarrierTerminalPickup: pickup.isDropAtTerminal,
  };
};

/**
 * Maps the freight-browser's flat `LtlAdditionalServices` to the is-prefixed backend
 * `FreightRequestAccessorials`. The browser only surfaces a subset of accessorials, so
 * only those fields are forwarded; everything else the backend type allows is omitted.
 * `isNotifyBeforeDelivery` intentionally maps from the browser's `isCallBeforeDelivery`
 * toggle (the browser has no separate notify-before-delivery field) and `isHoldAtTerminal`
 * has no browser source, so it stays false — both preserved from the prior payload.
 */
export const mapBrowserServicesToAccessorials = (services: LtlAdditionalServices): FreightRequestAccessorials => {
  return {
    isLiftgatePickup: services.isLiftgatePickup,
    isLiftgateDelivery: services.isLiftgateDelivery,
    isInsidePickup: services.isInsidePickup,
    isInsideDelivery: services.isInsideDelivery,
    isResidentialPickup: services.isResidentialPickup,
    isResidentialDelivery: services.isResidentialDelivery,
    isNotifyBeforePickup: services.isNotifyBeforePickup,
    isAppointmentDelivery: services.isAppointmentDelivery,
    isNotifyBeforeDelivery: services.isCallBeforeDelivery,
    isConstructionSitePickup: services.isConstructionSitePickup,
    isConstructionSiteDelivery: services.isConstructionSiteDelivery,
    isCarrierTerminalPickup: services.isCarrierTerminalPickup ?? false,
    isTradeshowPickup: services.isTradeShowPickup,
    isTradeshowDelivery: services.isTradeShowDelivery,
    tradeshowPickupName: null,
    tradeshowPickupBoothNumber: null,
    tradeshowDeliveryName: null,
    tradeshowDeliveryBoothNumber: null,
    isSortAndSegregate: services.isSortAndSegregate,
    isProtectionFromCold: services.isProtectFromFreezing,
    isHoldAtTerminal: false,
    isGroceryConsolidationPickup: services.isGroceryConsolidationPickup,
    isGroceryConsolidationDelivery: services.isGroceryConsolidationDelivery,
  };
};

/**
 * Maps the wizard insurance shape to the backend `FreightInsurance`, or null when
 * insurance is not requested.
 */
export const mapWizardInsurance = (insurance: WizardInsurance): FreightInsurance | null => {
  if (!insurance.isInsured) return null;
  return {
    requestInsurance: true,
    declaredValue: insurance.insuredValue,
    commodityCategory: (insurance.category || null) as InsuranceCommodityCategory | null,
    itemCondition: (insurance.conditionOfGoods || null) as InsuranceItemCondition | null,
    marksNumbers: insurance.marksAndNumbers || null,
  };
};

/**
 * Maps a backend `FreightInsurance` to the wizard `WizardInsurance` shape — the
 * inverse of `mapWizardInsurance`. Returns all-default insurance when the backend
 * source is null (i.e. insurance was not requested).
 */
export const mapBackendInsuranceToWizard = (insurance: FreightInsurance | null): WizardInsurance => ({
  isInsured: insurance?.requestInsurance ?? false,
  insuredValue: insurance?.declaredValue ?? 0,
  category: insurance?.commodityCategory ?? '',
  conditionOfGoods: insurance?.itemCondition ?? '',
  marksAndNumbers: insurance?.marksNumbers ?? '',
});

/**
 * Maps the wizard handling-charge toggle to the backend `FreightHandlingCharge`, or
 * null when disabled.
 */
export const mapWizardHandlingCharge = (charge: WizardHandlingCharge): FreightHandlingCharge | null => {
  if (!charge.isEnabled) return null;
  return { value: charge.value, unit: 'amount' };
};

/**
 * Maps the wizard references list to the backend `FreightReference[]`. Returns an
 * empty array when references are disabled.
 */
export const mapWizardReferences = (references: WizardReferences): FreightReference[] => {
  if (!references.isEnabled) return [];
  return references.items.map((item) => ({ type: item.type || null, value: item.value || null }));
};

// ---------------------------------------------------------------------------
// Reverse hydration mappers (backend FreightShipment -> wizard form state).
// These are the faithful inverse of the forward mappers above and a port of the
// old freight-quote-context's `buildLoadedQuoteData` / `mapRaw*` intent. Pure and
// store-agnostic (no ss-ui-client imports).
// ---------------------------------------------------------------------------

/**
 * Maps a backend `FreightAddress` to the wizard/browser `LtlAddress`. Every
 * nullable string field defaults to `''`; `countryCode` defaults to `'US'`. The
 * typed `FreightAddress` has no email field, so `email` is set to `''`; `line3`
 * is ignored (the `LtlAddress` shape has no third line).
 */
export const mapBackendAddressToLtl = (addr: FreightAddress | null): LtlAddress => {
  return {
    name: addr?.name ?? '',
    company: addr?.company ?? '',
    line1: addr?.line1 ?? '',
    line2: addr?.line2 ?? '',
    city: addr?.city ?? '',
    state: addr?.state ?? '',
    postalCode: addr?.postalCode ?? '',
    countryCode: addr?.countryCode ?? 'US',
    phone: addr?.phone ?? '',
    // email will be non-null once the backend persists it at booking time.
    email: addr?.email ?? '',
    locationType: addr?.locationType ?? '',
    // Backend freight addresses carry no warehouse timezone; callers that resolve
    // the origin from the Redux ship-from list set ianaTimeZone directly.
    ianaTimeZone: null,
    verificationStatus: addr?.verificationStatus ?? null,
    verificationMessage: addr?.verificationMessage ?? null,
    verificationUtc: addr?.verificationUtc ?? null,
    lockAddress: addr?.lockAddress ?? false,
  };
};

/**
 * Maps a backend `FreightQuote` to the wizard/browser `LtlQuote`. Straight field
 * copy with per-field defaults (mirror of the old `mapRawQuoteOptions`).
 */
export const mapBackendQuoteToLtl = (q: FreightQuote): LtlQuote => {
  return {
    offerId: q.offerId ?? '',
    productTransactionId: q.productTransactionId ?? '',
    carrierName: q.carrierName ?? '',
    carrierScac: q.carrierScac ?? q.carrierCode ?? '',
    serviceName: q.serviceName ?? '',
    totalCharges: q.totalCharges ?? 0,
    transitDays: q.transitDays ?? 0,
    estimatedDeliveryDate: q.estimatedDeliveryDate ?? '',
    isGuaranteed: q.isGuaranteed ?? false,
    quoteExpirationDate: q.quoteExpirationDate ?? null,
    serviceType: q.serviceType ?? null,
    originTerminalCode: q.originTerminalCode ?? null,
    destinationTerminalCode: q.destinationTerminalCode ?? null,
    insuredAmount: q.insuredAmount ?? null,
    insurancePremium: q.insurancePremium ?? null,
    insuranceCertificateNumber: q.insuranceCertificateNumber ?? null,
    charges: q.charges ?? null,
    totalHandlingCharge: q.totalHandlingCharge ?? null,
    transitDistance: q.transitDistance ?? null,
    transitDistanceUnit: q.transitDistanceUnit ?? null,
    scacList: q.scacList ?? [],
    insuranceDeductible: q.insuranceDeductible ?? null,
    insuranceTermsAndConditions: q.insuranceTermsAndConditions ?? null,
    insuranceQuoteDate: q.insuranceQuoteDate ?? null,
    insuranceQuoteExpirationDate: q.insuranceQuoteExpirationDate ?? null,
    maxLiabilityNew: q.maxLiabilityNew ?? null,
    maxLiabilityUsed: q.maxLiabilityUsed ?? null,
    pickupDays: q.pickupDays ?? [],
    latestPickupTime: q.latestPickupTime ?? null,
    pickupWindowMinutes: q.pickupWindowMinutes ?? null,
    notificationWindowMinutes: q.notificationWindowMinutes ?? null,
    businessHours: q.businessHours ?? [],
  };
};

/**
 * Maps the backend handling units to the wizard `LtlHandlingUnit[]` (mirror of the
 * old `mapRawHandlingUnits`): ids are 1-based index strings, packagingType defaults
 * to 'Pallet', commodity freightClass to '_50', etc. Only the commodity fields that map
 * cleanly are copied (description, freightClass, nmfcCode) — no fields are invented.
 */
export const mapBackendHandlingUnitsToLtl = (units: FreightHandlingUnit[] | null): LtlHandlingUnit[] => {
  return (units ?? []).map((hu, index) => ({
    id: String(index + 1),
    handlingUnitId: hu.handlingUnitId ?? null,
    packagingType: hu.packagingType ?? 'Pallet',
    quantity: hu.quantity ?? 1,
    length: hu.length ?? 0,
    width: hu.width ?? 0,
    height: hu.height ?? 0,
    dimensionUnit: hu.dimensionUnit,
    isStackable: hu.isStackable ?? false,
    commodities: (hu.commodities ?? []).map((c, cIndex) => ({
      id: String(cIndex + 1),
      commodityId: c.commodityId ?? null,
      description: c.description ?? '',
      freightClass: (c.freightClass ?? '_50') as FreightClass,
      nmfcCode: c.nmfcCode ?? '',
      quantity: 1,
      numberOfPieces: c.quantity ?? undefined,
      pieceType: c.packageType ?? undefined,
      weight: c.weight ?? 0,
      // FreightWeightUnit includes 'Unknown', which is not a valid LtlCommodity weight
      // unit — collapse it to undefined so the unit defaulting elsewhere applies.
      weightUnit: c.weightUnit && c.weightUnit !== 'Unknown' ? c.weightUnit : undefined,
      isHazmat: c.isHazmat ?? false,
      hazmatDetails: null,
      hazmatInfo: c.hazmatInfo ?? null,
      insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
    })),
  }));
};

/**
 * Maps backend `FreightAccessorials` to the four narrow wizard service groups — the
 * inverse of `mapWizardServicesToAccessorials`. Wizard fields with no backend source
 * (e.g. `isNotifyBeforePickup`, `isDropAtTerminal`) default to `false`;
 * see the forward mapper's matching TODOs.
 *
 * `destinationAddress` is needed to derive `deliveryServices.isLimitedAccess` —
 * that value lives on the address's `locationType` rather than on the accessorials.
 */
export const mapAccessorialsToWizardServices = (
  acc: FreightAccessorials | null,
  destinationAddress: FreightAddress | null,
): {
  pickupServices: WizardPickupServices;
  deliveryServices: WizardDeliveryServices;
  shipmentServices: WizardShipmentServices;
  tradeshow: WizardTradeshow;
  tradeshowPickup: WizardTradeshow;
} => {
  return {
    pickupServices: {
      isInsidePickup: acc?.isInsidePickup ?? false,
      isConstructionSitePickup: acc?.isConstructionSitePickup ?? false,
      isLiftgatePickup: acc?.isLiftgatePickup ?? false,
      isNotifyBeforePickup: acc?.isNotifyBeforePickup ?? false,
      isResidentialPickup: acc?.isResidentialPickup ?? false,
      isDropAtTerminal: acc?.isCarrierTerminalPickup ?? false,
      isGroceryConsolidationPickup: acc?.isGroceryConsolidationPickup ?? false,
      // TODO: isLimitedAccess is derived from the address's locationType property, but we have no way to preserve that sort of data on the ship from warehouse's address
      isLimitedAccess: false,
      isTradeshowPickup: acc?.isTradeshowPickup ?? false,
    },
    deliveryServices: {
      isInsideDelivery: acc?.isInsideDelivery ?? false,
      isConstructionSiteDelivery: acc?.isConstructionSiteDelivery ?? false,
      isLiftgateDelivery: acc?.isLiftgateDelivery ?? false,
      isNotifyBeforeDelivery: acc?.isNotifyBeforeDelivery ?? false,
      isResidentialDelivery: acc?.isResidentialDelivery ?? false,
      isHoldAtTerminal: acc?.isHoldAtTerminal ?? false,
      isGroceryConsolidationDelivery: acc?.isGroceryConsolidationDelivery ?? false,
      isAppointmentDelivery: acc?.isAppointmentDelivery ?? false,
      isLimitedAccess: destinationAddress?.locationType === LIMITED_ACCESS,
      isTradeshowDelivery: acc?.isTradeshowDelivery ?? false,
    },
    shipmentServices: {
      isSortAndSegregate: acc?.isSortAndSegregate ?? false,
      isProtectFromFreeze: acc?.isProtectionFromCold ?? false,
    },
    tradeshow: {
      name: acc?.tradeshowDeliveryName ?? '',
      boothNumber: acc?.tradeshowDeliveryBoothNumber ?? '',
    },
    tradeshowPickup: {
      name: acc?.tradeshowPickupName ?? '',
      boothNumber: acc?.tradeshowPickupBoothNumber ?? '',
    },
  };
};

/**
 * Maps a backend `FreightAccessorials` to the full `LtlAdditionalServices` shape used
 * by the freight-details view. Fields with a real backend source are coalesced
 * `?? false` (note the renames `isTradeshow*` → `isTradeShow*` and `isProtectionFromCold`
 * → `isProtectFromFreezing`). Every other `LtlAdditionalServices` field has NO backend
 * source on `FreightAccessorials` and defaults to `false`: isLimitedAccessPickup,
 * isFlatbedPickup, isDockLevelPickup, isTopLoadPickup,
 * isLimitedAccessDelivery, isFlatbedDelivery, isDockLevelDelivery, isCallBeforeDelivery,
 * isHazmat, isTemperatureControl, isOverlength8to12ft, isOverlength12to20ft,
 * isOverlength20ftPlus, isGuaranteedDelivery, isBlindShipment, isMarkAndTag, isCOD.
 */
export const mapBackendAccessorialsToLtl = (acc: FreightAccessorials | null): LtlAdditionalServices => {
  return {
    isLiftgatePickup: acc?.isLiftgatePickup ?? false,
    isInsidePickup: acc?.isInsidePickup ?? false,
    isResidentialPickup: acc?.isResidentialPickup ?? false,
    isLimitedAccessPickup: false,
    isFlatbedPickup: false,
    isDockLevelPickup: false,
    isTopLoadPickup: false,
    isConstructionSitePickup: acc?.isConstructionSitePickup ?? false,
    isTradeShowPickup: acc?.isTradeshowPickup ?? false,
    tradeshowPickupName: acc?.tradeshowPickupName ?? null,
    tradeshowPickupBoothNumber: acc?.tradeshowPickupBoothNumber ?? null,
    isGroceryConsolidationPickup: acc?.isGroceryConsolidationPickup ?? false,
    isCarrierTerminalPickup: acc?.isCarrierTerminalPickup ?? false,
    isNotifyBeforePickup: acc?.isNotifyBeforePickup ?? false,
    isLiftgateDelivery: acc?.isLiftgateDelivery ?? false,
    isInsideDelivery: acc?.isInsideDelivery ?? false,
    isResidentialDelivery: acc?.isResidentialDelivery ?? false,
    isLimitedAccessDelivery: false,
    isFlatbedDelivery: false,
    isDockLevelDelivery: false,
    isConstructionSiteDelivery: acc?.isConstructionSiteDelivery ?? false,
    isTradeShowDelivery: acc?.isTradeshowDelivery ?? false,
    tradeshowDeliveryName: acc?.tradeshowDeliveryName ?? null,
    tradeshowDeliveryBoothNumber: acc?.tradeshowDeliveryBoothNumber ?? null,
    isGroceryConsolidationDelivery: acc?.isGroceryConsolidationDelivery ?? false,
    isAppointmentDelivery: acc?.isAppointmentDelivery ?? false,
    isHoldAtTerminal: acc?.isHoldAtTerminal ?? false,
    isCallBeforeDelivery: false,
    isSortAndSegregate: acc?.isSortAndSegregate ?? false,
    isNotifyBeforeDelivery: acc?.isNotifyBeforeDelivery ?? false,
    isHazmat: false,
    isTemperatureControl: false,
    isProtectFromFreezing: acc?.isProtectionFromCold ?? false,
    isOverlength8to12ft: false,
    isOverlength12to20ft: false,
    isOverlength20ftPlus: false,
    isGuaranteedDelivery: false,
    isBlindShipment: false,
    isMarkAndTag: false,
    isCOD: false,
  };
};

/**
 * A canonical empty `FreightBookingFormState` — the starting point for hydration and
 * for NEW mode. Mirrors the default times / all-false services the old reducer seeded.
 * `displayWeightUnit` is the seller's preferred display weight unit, used as the
 * fallback for the seed handling unit's commodity weight unit (rather than a
 * hardcoded literal).
 */
export const createEmptyFormState = (
  sellerFreightProviderID: number | undefined,
  displayWeightUnit: WeightUnit,
  displayDimensionUnit: DimensionUnit,
): FreightBookingFormState => ({
  selectedShipFromId: '',
  originAddress: mapBackendAddressToLtl(null),
  shipmentDate: '',
  pickupServices: {
    isInsidePickup: false,
    isConstructionSitePickup: false,
    isLiftgatePickup: false,
    isNotifyBeforePickup: false,
    isResidentialPickup: false,
    isDropAtTerminal: false,
    isGroceryConsolidationPickup: false,
    isLimitedAccess: false,
    isTradeshowPickup: false,
  },
  tradeshowPickup: { name: '', boothNumber: '' },
  destinationAddress: mapBackendAddressToLtl(null),
  shouldSaveAsNewCustomer: false,
  deliveryServices: {
    isInsideDelivery: false,
    isConstructionSiteDelivery: false,
    isLiftgateDelivery: false,
    isNotifyBeforeDelivery: false,
    isResidentialDelivery: false,
    isHoldAtTerminal: false,
    isGroceryConsolidationDelivery: false,
    isAppointmentDelivery: false,
    isLimitedAccess: false,
    isTradeshowDelivery: false,
  },
  tradeshow: { name: '', boothNumber: '' },
  handlingUnits: [createEmptyHandlingUnit([], displayWeightUnit, displayDimensionUnit)],
  shipmentServices: { isSortAndSegregate: false, isProtectFromFreeze: false },
  pickupReadyTime: '',
  pickupCloseTime: '',
  willArrangePickupSeparately: false,
  insurance: { isInsured: false, conditionOfGoods: '', category: '', insuredValue: 0, marksAndNumbers: '' },
  handlingCharge: { isEnabled: false, value: 0 },
  references: { isEnabled: false, items: [] },
  pickupInstructions: '',
  deliveryInstructions: '',
  handlingInstructions: '',
  sellerFreightProviderID,
});

/**
 * TODO: this looks unused
 * Orchestrator: hydrates a `FreightBookingFormState` (plus rates + selected rate)
 * from a backend `FreightShipment`. Port of the old `buildLoadedQuoteData` +
 * `LOAD_SAVED_QUOTE_SUCCESS` reducer intent, adapted to the new (no-'quotes'-step)
 * wizard.
 */
export const buildFormStateFromShipment = (
  shipment: FreightShipment,
  displayWeightUnit: WeightUnit,
  displayDimensionUnit: DimensionUnit,
): { formState: FreightBookingFormState; rates: LtlQuote[]; selectedRate: LtlQuote | null } => {
  const services = mapAccessorialsToWizardServices(shipment.additionalServices ?? null, shipment.destinationAddress ?? null);
  const handlingUnits = mapBackendHandlingUnitsToLtl(shipment.handlingUnits ?? null);

  const formState: FreightBookingFormState = {
    ...createEmptyFormState(shipment.sellerFreightProviderId, displayWeightUnit, displayDimensionUnit),
    selectedShipFromId: String(shipment.shipFromWarehouseId ?? ''),
    // NOTE: the typed FreightShipment exposes no origin address; originAddress stays
    // the empty default and the container resolves origin from selectedShipFromId at
    // payload time (see resolveOriginAddress).
    destinationAddress: mapBackendAddressToLtl(shipment.destinationAddress),
    shipmentDate: shipment.shipDate ?? '',
    handlingUnits: handlingUnits.length > 0 ? handlingUnits : [createEmptyHandlingUnit([], displayWeightUnit, displayDimensionUnit)],
    pickupServices: services.pickupServices,
    deliveryServices: services.deliveryServices,
    shipmentServices: services.shipmentServices,
    tradeshow: services.tradeshow,
    tradeshowPickup: services.tradeshowPickup,
  };

  const rates = (shipment.savedQuoteOptions ?? []).map(mapBackendQuoteToLtl);
  const selectedRate = shipment.selectedOfferId ? (rates.find((r) => r.offerId === shipment.selectedOfferId) ?? null) : null;

  return { formState, rates, selectedRate };
};

/**
 * Maps a backend `FreightDocument` to the freight-details `LtlDocument` view model.
 * Straight field copy — both shapes are identical (documentType, fileName, key,
 * createDate).
 */
export const mapBackendDocumentToLtl = (doc: FreightDocument): LtlDocument => ({
  documentType: doc.documentType,
  fileName: doc.fileName,
  key: doc.key,
  createDate: doc.createDate,
});

/**
 * Maps a backend `FreightShipment` (plus an already-resolved origin `LtlAddress`,
 * since the typed `FreightShipment` exposes no origin address — see §7.3 / the
 * container) to the freight-details `LtlShipment` view model. Scalar fields default
 * via `?? <empty>`; `destinationAddress`, `handlingUnits`, and `additionalServices`
 * reuse the existing reverse mappers. Optional `savedQuoteOptions` / `selectedOfferId`
 * stay `undefined` when the backend source is null.
 */
export const mapBackendShipmentToLtlShipment = (shipment: FreightShipment, originAddress: LtlAddress | null): LtlShipment => {
  return {
    freightShipmentId: shipment.freightShipmentId,
    sellerFreightProviderId: shipment.sellerFreightProviderId ?? 0,
    providerCode: shipment.providerCode ?? '',
    productTransactionId: shipment.productTransactionId ?? '',
    offerId: shipment.offerId ?? '',
    status: shipment.status,
    carrierName: shipment.carrierName ?? '',
    carrierScac: shipment.carrierScac,
    serviceName: shipment.serviceName ?? '',
    totalCharges: shipment.totalCharges ?? 0,
    transitDays: shipment.transitDays ?? 0,
    estimatedDeliveryDate: shipment.estimatedDeliveryDate ?? '',
    totalWeight: shipment.totalWeight ?? 0,
    shipDate: shipment.shipDate ?? '',
    originAddress,
    destinationAddress: mapBackendAddressToLtl(shipment.destinationAddress),
    handlingUnits: mapBackendHandlingUnitsToLtl(shipment.handlingUnits),
    documents: (shipment.documents ?? []).map(mapBackendDocumentToLtl),
    additionalServices: mapBackendAccessorialsToLtl(shipment.additionalServices),
    bolNumber: shipment.bolNumber,
    proNumber: shipment.proNumber,
    confirmationNumber: shipment.confirmationNumber,
    quoteExpirationDate: shipment.quoteExpirationDate,
    shipFromWarehouseId: shipment.shipFromWarehouseId,
    createDate: shipment.createDate,
    savedQuoteOptions: shipment.savedQuoteOptions?.map(mapBackendQuoteToLtl),
    selectedOfferId: shipment.selectedOfferId ?? undefined,
    pickupReadyTime: shipment.pickupDetails?.readyTime ?? null,
    pickupCloseTime: shipment.pickupDetails?.closeTime ?? null,
    // Fall back to shipDate when pickupDate is absent so the Pickup Window always has a date.
    pickupDate: shipment.pickupDetails?.pickupDate ?? shipment.shipDate ?? null,
    pickupInstructions: shipment.pickupDetails?.pickupInstructions ?? null,
    deliveryInstructions: shipment.pickupDetails?.deliveryInstructions ?? null,
    handlingInstructions: shipment.pickupDetails?.handlingInstructions ?? null,
    handlingCharge: shipment.handlingCharge ?? null,
    insurance: shipment.insurance
      ? {
          isInsured: shipment.insurance.requestInsurance,
          commodityCategoryCode: shipment.insurance.commodityCategory ?? '',
          condition: shipment.insurance.itemCondition ?? '',
          declaredValue: shipment.insurance.declaredValue,
          marksNumbers: shipment.insurance.marksNumbers ?? null,
        }
      : null,
    references: (shipment.references ?? []).map((r) => ({ type: r.type ?? null, value: r.value ?? null })),
    freightTrackingStatusId: shipment.freightTrackingStatusId ?? null,
    lastTrackingEventUtc: shipment.lastTrackingEventUtc ?? null,
  };
};

const mapFulfillmentPlanAddressToLtl = (addr: FulfillmentPlanAddress | null, email: string | null): LtlAddress => {
  return {
    name: addr?.name ?? '',
    company: addr?.company ?? '',
    line1: addr?.line1 ?? '',
    line2: addr?.line2 ?? '',
    city: addr?.city ?? '',
    state: addr?.state ?? '',
    postalCode: addr?.postalCode ?? '',
    countryCode: addr?.countryCode ?? 'US',
    phone: addr?.phone ?? '',
    email: email ?? '',
    locationType: '',
    ianaTimeZone: null,
    verificationStatus: addr?.verificationStatus ?? null,
    verificationMessage: addr?.verificationMessage ?? null,
    verificationUtc: addr?.verificationUtc ?? null,
    lockAddress: addr?.lockAddress ?? false,
  };
};

export const buildFormStateFromFulfillmentPlan = (
  fp: FulfillmentPlan,
  sellerFreightProviderId: number | undefined,
  displayWeightUnit: WeightUnit,
  displayDimensionUnit: DimensionUnit,
): { formState: FreightBookingFormState; rates: LtlQuote[]; selectedRate: LtlQuote | null } => {
  const fc = fp.freightConfiguration;

  const handlingUnits = mapBackendHandlingUnitsToLtl(
    (fc?.handlingUnits ?? []).map((hu) => ({
      handlingUnitId: hu.handlingUnitId,
      packagingType: (hu.packagingType ?? 'Pallet') as FreightPackagingType,
      quantity: hu.quantity,
      length: hu.length,
      width: hu.width,
      height: hu.height,
      dimensionUnit: (hu.dimensionUnit ?? 'Inches') as FreightDimensionUnit,
      weightUnit: (hu.weightUnit ?? null) as FreightWeightUnit | null,
      isStackable: hu.isStackable,
      commodities: (hu.commodities ?? []).map((c) => ({
        commodityId: c.commodityId,
        freightClass: (c.freightClass ?? '_50') as FreightClass,
        description: c.description,
        packageType: c.packageType,
        quantity: c.quantity,
        weight: c.weight,
        weightUnit: (c.weightUnit ?? 'Unknown') as FreightWeightUnit,
        nmfcCode: c.nmfcCode,
        isHazmat: c.isHazmat,
        hazmatInfo: c.hazmatInfo
          ? {
              hazIdType: c.hazmatInfo.hazIdType as HazmatIdType | null,
              identificationNumber: c.hazmatInfo.identificationNumber,
              properShippingName: c.hazmatInfo.properShippingName,
              hazardClass: c.hazmatInfo.hazardClass as FreightHazardClass[] | null,
              packingGroup: c.hazmatInfo.packingGroup as PackingGroup | null,
              emergencyPhone: c.hazmatInfo.emergencyPhone,
              emergencyContactName: c.hazmatInfo.emergencyContactName,
              emergencyResponseReference: c.hazmatInfo.emergencyResponseReference ?? null,
              flashpointTemperatureF: c.hazmatInfo.flashpointTemperatureF ?? null,
              additionalDetails: c.hazmatInfo.additionalDetails ?? null,
            }
          : null,
      })),
    })),
  );

  const services = mapAccessorialsToWizardServices(fc?.additionalServices ?? null, null);

  const emptyState = createEmptyFormState(fc?.sellerFreightProviderID ?? sellerFreightProviderId, displayWeightUnit, displayDimensionUnit);

  const formState: FreightBookingFormState = {
    ...emptyState,
    selectedShipFromId: fc?.shipFromId ?? '',
    destinationAddress: mapFulfillmentPlanAddressToLtl(fc?.shipTo ?? null, fc?.email ?? null),
    shipmentDate: fc?.shipDate ? fc.shipDate.split('T')[0] : DateTime.now().toFormat('yyyy-MM-dd'),
    handlingUnits: handlingUnits.length > 0 ? handlingUnits : [createEmptyHandlingUnit([], displayWeightUnit, displayDimensionUnit)],
    pickupServices: {
      ...services.pickupServices,
      isLimitedAccess: fc?.pickupDetails?.locationType === LIMITED_ACCESS,
    },
    deliveryServices: {
      ...services.deliveryServices,
      isLimitedAccess: fc?.shipTo?.locationType === LIMITED_ACCESS,
    },
    shipmentServices: services.shipmentServices,
    tradeshow: services.tradeshow,
    tradeshowPickup: services.tradeshowPickup,
    insurance: mapBackendInsuranceToWizard(fc?.insurance ?? null),
    pickupReadyTime: fc?.pickupDetails?.readyTime?.slice(0, 5) || emptyState.pickupReadyTime,
    pickupCloseTime: fc?.pickupDetails?.closeTime?.slice(0, 5) || emptyState.pickupCloseTime,
    willArrangePickupSeparately: fc?.pickupDetails?.isSelfScheduled ?? false,
    pickupInstructions: fc?.pickupInstructions ?? emptyState.pickupInstructions,
    deliveryInstructions: fc?.deliveryInstructions ?? emptyState.deliveryInstructions,
    handlingInstructions: fc?.handlingInstructions ?? emptyState.handlingInstructions,
    references: fc?.references?.length
      ? {
          isEnabled: true,
          items: fc.references.map((r, i) => ({ id: String(i + 1), type: r.type ?? '', value: r.value ?? '' })),
        }
      : emptyState.references,
    handlingCharge: fc?.handlingCharge ? { isEnabled: true, value: fc.handlingCharge.value } : emptyState.handlingCharge,
  };

  const matchedQuote = fc?.offerId ? fc.allQuotes?.find((q) => q.offerId === fc.offerId) : undefined;
  const legacySelectedRate: LtlQuote | null = fc?.offerId
    ? {
        offerId: fc.offerId,
        productTransactionId: '',
        carrierName: fc.carrierName ?? '',
        carrierScac: fc.carrierScac ?? '',
        serviceName: fc.serviceName ?? '',
        totalCharges: fc.totalCharges ?? 0,
        transitDays: fc.transitDays ?? 0,
        estimatedDeliveryDate: fc.estimatedDeliveryDateUtc ?? '',
        isGuaranteed: false,
        quoteExpirationDate: fc.quoteExpirationDateUtc ?? null,
        serviceType: null,
        originTerminalCode: null,
        destinationTerminalCode: null,
        insuredAmount: null,
        insurancePremium: null,
        insuranceCertificateNumber: null,
        charges: null,
        totalHandlingCharge: null,
        transitDistance: null,
        transitDistanceUnit: null,
        scacList: [],
        insuranceDeductible: null,
        insuranceTermsAndConditions: null,
        insuranceQuoteDate: null,
        insuranceQuoteExpirationDate: null,
        maxLiabilityNew: null,
        maxLiabilityUsed: null,
        pickupDays: [],
        latestPickupTime: null,
        pickupWindowMinutes: null,
        notificationWindowMinutes: null,
        businessHours: [],
      }
    : null;
  const selectedRate = matchedQuote ? mapBackendQuoteToLtl(matchedQuote) : legacySelectedRate;

  const rates = fc?.allQuotes?.map(mapBackendQuoteToLtl) ?? (selectedRate ? [selectedRate] : []);

  return { formState, rates, selectedRate };
};

/**
 * Builds a `FreightFulfillmentPlanSaveQuoteRequest` from the wizard form state, rates,
 * and selected rate. `sellerFreightProviderId` must be resolved by the caller.
 */
export const buildSaveQuoteToFulfillmentPlanPayload = (
  fulfillmentPlanId: string | null,
  formState: FreightBookingFormState,
  allQuotes: LtlQuote[],
  selectedRate: LtlQuote | null,
  weightUnit: FreightWeightUnit,
  dimensionUnit: FreightRequestDimensionUnit,
  sellerFreightProviderId?: number,
): FreightFulfillmentPlanSaveQuoteRequest => {
  const totalWeight =
    formState.handlingUnits?.reduce((sum, hu) => sum + (hu.commodities?.reduce((cSum, c) => cSum + (c.weight || 0), 0) || 0), 0) ?? 0;

  return {
    email: formState?.destinationAddress?.email ?? null,
    fulfillmentPlanId,
    freightNumber: null,
    sellerFreightProviderId,
    productTransactionId: allQuotes[0]?.productTransactionId,
    offerId: selectedRate?.offerId ?? null,
    carrierName: selectedRate?.carrierName ?? null,
    carrierScac: selectedRate?.carrierScac ?? null,
    serviceName: selectedRate?.serviceName ?? null,
    totalCharges: selectedRate?.totalCharges ?? null,
    transitDays: selectedRate?.transitDays ?? null,
    estimatedDeliveryDate: selectedRate?.estimatedDeliveryDate ?? null,
    totalWeight,
    shipDate: formState.shipmentDate || null,
    shipFromWarehouseId: formState.selectedShipFromId ? Number(formState.selectedShipFromId) : null,
    destinationAddress: mapAddressToBackend(formState.destinationAddress, formState.deliveryServices.isLimitedAccess),
    handlingUnits: mapHandlingUnitsToPayload(formState.handlingUnits, weightUnit, dimensionUnit),
    additionalServices: mapWizardServicesToAccessorials(
      formState.pickupServices,
      formState.deliveryServices,
      formState.shipmentServices,
      formState.tradeshow,
      formState.tradeshowPickup,
    ),
    insurance: mapWizardInsurance(formState.insurance),
    pickupDetails: {
      pickupDate:
        formState.shipmentDate && formState.pickupReadyTime
          ? `${formState.shipmentDate.split('T')[0]}T${formState.pickupReadyTime}:00`
          : null,
      readyTime: formState.pickupReadyTime ? `${formState.pickupReadyTime}:00` : null,
      closeTime: formState.pickupCloseTime ? `${formState.pickupCloseTime}:00` : null,
      isSelfScheduled: formState.willArrangePickupSeparately,
      locationType: formState.pickupServices.isLimitedAccess ? LIMITED_ACCESS : null,
    },
    pickupInstructions: formState.pickupInstructions || null,
    deliveryInstructions: formState.deliveryInstructions || null,
    handlingInstructions: formState.handlingInstructions || null,
    allQuotes: allQuotes.length
      ? allQuotes.map((q) => ({
          offerId: q.offerId,
          productTransactionId: q.productTransactionId,
          carrierName: q.carrierName,
          carrierScac: q.carrierScac,
          carrierCode: q.carrierScac,
          serviceName: q.serviceName,
          totalCharges: q.totalCharges,
          totalHandlingCharge: q.totalHandlingCharge ?? null,
          transitDays: q.transitDays,
          estimatedDeliveryDate: q.estimatedDeliveryDate,
          isGuaranteed: q.isGuaranteed,
          serviceType: q.serviceType ?? null,
          originTerminalCode: q.originTerminalCode ?? null,
          destinationTerminalCode: q.destinationTerminalCode ?? null,
          transitDistance: q.transitDistance ?? null,
          transitDistanceUnit: q.transitDistanceUnit ?? null,
          scacList: q.scacList ?? [],
          charges: q.charges ?? [],
          insuredAmount: q.insuredAmount ?? null,
          insurancePremium: q.insurancePremium ?? null,
          insuranceCertificateNumber: q.insuranceCertificateNumber ?? null,
          insuranceDeductible: q.insuranceDeductible ?? null,
          insuranceTermsAndConditions: q.insuranceTermsAndConditions ?? null,
          insuranceQuoteDate: q.insuranceQuoteDate ?? null,
          insuranceQuoteExpirationDate: q.insuranceQuoteExpirationDate ?? null,
          quoteExpirationDate: q.quoteExpirationDate ?? null,
          maxLiabilityNew: q.maxLiabilityNew ?? null,
          maxLiabilityUsed: q.maxLiabilityUsed ?? null,
          pickupDays: q.pickupDays ?? [],
          latestPickupTime: q.latestPickupTime ?? null,
          pickupWindowMinutes: q.pickupWindowMinutes ?? null,
          notificationWindowMinutes: q.notificationWindowMinutes ?? null,
          businessHours: q.businessHours ?? [],
        }))
      : null,
  };
};

/**
 * Builds a `SaveFreightConfigurationRequest` from the wizard form state and selected
 * rate. `freightFulfillmentPlanStatusID` and `freightNumber` are read from the
 * FulfillmentPlan already in memory (caller resolves them from the entity store).
 * Returns `null` when there is no fulfillment plan to update (fulfillmentPlanId is falsy).
 */
export const buildSaveConfigurationPayload = (
  fulfillmentPlanId: string | null,
  formState: FreightBookingFormState,
  selectedRate: LtlQuote | null,
  allQuotes: LtlQuote[],
  weightUnit: FreightWeightUnit,
  dimensionUnit: FreightRequestDimensionUnit,
  freightFulfillmentPlanStatusID: number | null,
  freightNumber: string | null,
): SaveFreightConfigurationRequest | null => {
  if (!fulfillmentPlanId) return null;

  return {
    email: formState?.destinationAddress?.email ?? null,
    freightFulfillmentPlanStatusID,
    freightNumber: freightNumber ?? '',
    sellerFreightProviderID: formState.sellerFreightProviderID || undefined,
    offerId: selectedRate?.offerId ?? null,
    carrierName: selectedRate?.carrierName ?? null,
    carrierScac: selectedRate?.carrierScac ?? null,
    serviceName: selectedRate?.serviceName ?? null,
    totalCharges: selectedRate?.totalCharges ?? null,
    transitDays: selectedRate?.transitDays ?? null,
    estimatedDeliveryDateUtc: selectedRate?.estimatedDeliveryDate ?? null,
    pickupDateUtc: null,
    quoteExpirationDateUtc: selectedRate?.quoteExpirationDate ?? null,
    totalWeight:
      formState.handlingUnits?.reduce((sum, hu) => sum + (hu.commodities?.reduce((cSum, c) => cSum + (c.weight || 0), 0) || 0), 0) ?? 0,
    totalWeightUnitOfMeasureID: null,
    shipTo: mapAddressToBackend(formState.destinationAddress, formState.deliveryServices.isLimitedAccess),
    handlingUnits: mapHandlingUnitsToPayload(formState.handlingUnits, weightUnit, dimensionUnit),
    additionalServices: mapWizardServicesToAccessorials(
      formState.pickupServices,
      formState.deliveryServices,
      formState.shipmentServices,
      formState.tradeshow,
      formState.tradeshowPickup,
    ),
    insurance: mapWizardInsurance(formState.insurance),
    pickupDetails: {
      pickupDate:
        formState.shipmentDate && formState.pickupReadyTime
          ? `${formState.shipmentDate.split('T')[0]}T${formState.pickupReadyTime}:00`
          : null,
      readyTime: formState.pickupReadyTime ? `${formState.pickupReadyTime}:00` : null,
      closeTime: formState.pickupCloseTime ? `${formState.pickupCloseTime}:00` : null,
      isSelfScheduled: formState.willArrangePickupSeparately,
      locationType: formState.pickupServices.isLimitedAccess ? LIMITED_ACCESS : null,
    },
    shipFromId: formState.selectedShipFromId || null,
    shipDate: formState.shipmentDate || null,
    pickupInstructions: formState.pickupInstructions || null,
    deliveryInstructions: formState.deliveryInstructions || null,
    handlingInstructions: formState.handlingInstructions || null,
    references: mapWizardReferences(formState.references),
    handlingCharge: mapWizardHandlingCharge(formState.handlingCharge),
    allQuotes: allQuotes.length
      ? allQuotes.map((q) => ({
          offerId: q.offerId,
          productTransactionId: q.productTransactionId,
          carrierName: q.carrierName,
          carrierScac: q.carrierScac,
          carrierCode: q.carrierScac,
          serviceName: q.serviceName,
          totalCharges: q.totalCharges,
          totalHandlingCharge: q.totalHandlingCharge ?? null,
          transitDays: q.transitDays,
          estimatedDeliveryDate: q.estimatedDeliveryDate,
          isGuaranteed: q.isGuaranteed,
          serviceType: q.serviceType ?? null,
          originTerminalCode: q.originTerminalCode ?? null,
          destinationTerminalCode: q.destinationTerminalCode ?? null,
          transitDistance: q.transitDistance ?? null,
          transitDistanceUnit: q.transitDistanceUnit ?? null,
          scacList: q.scacList ?? [],
          charges: q.charges ?? [],
          insuredAmount: q.insuredAmount ?? null,
          insurancePremium: q.insurancePremium ?? null,
          insuranceCertificateNumber: q.insuranceCertificateNumber ?? null,
          insuranceDeductible: q.insuranceDeductible ?? null,
          insuranceTermsAndConditions: q.insuranceTermsAndConditions ?? null,
          insuranceQuoteDate: q.insuranceQuoteDate ?? null,
          insuranceQuoteExpirationDate: q.insuranceQuoteExpirationDate ?? null,
          quoteExpirationDate: q.quoteExpirationDate ?? null,
          maxLiabilityNew: q.maxLiabilityNew ?? null,
          maxLiabilityUsed: q.maxLiabilityUsed ?? null,
          pickupDays: q.pickupDays ?? [],
          latestPickupTime: q.latestPickupTime ?? null,
          pickupWindowMinutes: q.pickupWindowMinutes ?? null,
          notificationWindowMinutes: q.notificationWindowMinutes ?? null,
          businessHours: q.businessHours ?? [],
        }))
      : null,
  };
};

/**
 * Builds a `BookFreightFulfillmentPlanRequest` from the wizard form state and the
 * selected rate's `productTransactionId`. This is the sole canonical source for the
 * book payload — both `useBookFreight` and the wizard container delegate here.
 */
export const buildBookFulfillmentPlanPayload = (
  fulfillmentPlanId: string,
  formState: FreightBookingFormState,
  productTransactionId: string,
  overrideInventoryWarning?: boolean,
): BookFreightFulfillmentPlanRequest => {
  // Use `||` (not `??`) so an empty string also falls back to today.
  const pickupDate = (formState.shipmentDate || new Date().toISOString()).split('T')[0];
  // Pickup times are required by Tier 3 validation (getBookBlockingErrors) unless the seller
  // is self-scheduling the pickup, in which case the fields are cleared client-side and sent
  // as null — there is no meaningful pickup window when the carrier is arranged directly.
  const readyTime = formState.pickupReadyTime || null;
  const closeTime = formState.pickupCloseTime || null;

  return {
    fulfillmentPlanId,
    productTransactionId,
    bookingDetails: {
      destinationAddress: mapAddressToBackend(formState.destinationAddress, formState.deliveryServices.isLimitedAccess),
      references: mapWizardReferences(formState.references),
      pickupInstructions: formState.pickupInstructions,
      deliveryInstructions: formState.deliveryInstructions,
      handlingInstructions: formState.handlingInstructions,
      handlingCharge: mapWizardHandlingCharge(formState.handlingCharge),
    },
    pickupDetails: {
      pickupDate: readyTime ? `${pickupDate}T${readyTime}:00` : null,
      readyTime: readyTime ? `${readyTime}:00` : null,
      closeTime: closeTime ? `${closeTime}:00` : null,
      isSelfScheduled: formState.willArrangePickupSeparately,
      locationType: formState.pickupServices.isLimitedAccess ? LIMITED_ACCESS : null,
    },
    ...(overrideInventoryWarning !== undefined ? { overrideInventoryWarning } : {}),
  };
};
